import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import Marquee from "react-fast-marquee";
import {
  ClipboardCheck, GraduationCap, FileText, PenLine, MessagesSquare, Landmark, Plane, Home as HomeIcon,
  Zap, BadgeCheck, Scale, Globe2, BookOpen, ArrowRight, ArrowUpRight, Quote, Star, ChevronLeft, ChevronRight,
  MessageCircle, Sparkles,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MaskedLine, FadeUp, Stagger, StaggerItem, Counter, Magnetic, SmartImage } from "@/components/motion-primitives";
import { useConsult } from "@/components/ConsultProvider";
import { DEST_SLIDES, COUNTRIES, SERVICES, STATS, HIGHLIGHTS, TESTIMONIALS, SUCCESS_PHOTOS, FAQS, BLOGS, CONTACT } from "@/data/content";

const ICONS = { ClipboardCheck, GraduationCap, FileText, PenLine, MessagesSquare, Landmark, Plane, Home: HomeIcon, Zap, BadgeCheck, Scale, Globe2, BookOpen };

const Hero = () => {
  const { openConsult } = useConsult();
  const reduce = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const idleRef = useRef(null);

  const goTo = (i, manual = false) => {
    setIndex(((i % DEST_SLIDES.length) + DEST_SLIDES.length) % DEST_SLIDES.length);
    if (manual) {
      setPaused(true);
      clearTimeout(idleRef.current);
      idleRef.current = setTimeout(() => setPaused(false), 5000);
    }
  };

  useEffect(() => {
    if (paused || reduce) return undefined;
    const id = setInterval(() => setIndex((i) => (i + 1) % DEST_SLIDES.length), 4500);
    return () => clearInterval(id);
  }, [paused, reduce]);

  useEffect(() => () => clearTimeout(idleRef.current), []);

  const slide = DEST_SLIDES[index];

  return (
    <section
      className="relative flex min-h-[100svh] items-center overflow-hidden bg-navy text-white"
      data-testid="hero-section"
      aria-roledescription="carousel"
      aria-label="Study destinations showcase"
    >
      {DEST_SLIDES.map((s, i) => (
        <motion.div
          key={s.name}
          className="absolute inset-0"
          initial={false}
          animate={{ opacity: i === index ? 1 : 0, scale: i === index ? 1 : 1.06 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          aria-hidden={i !== index}
        >
          <img
            src={s.img}
            alt={s.alt}
            className="h-full w-full object-cover object-center"
            loading={i === 0 ? "eager" : "lazy"}
            data-testid={i === index ? "hero-slide-active" : `hero-slide-${i}`}
          />
        </motion.div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/45 to-black/85" aria-hidden="true" />
      <div className="noise-overlay absolute inset-0" aria-hidden="true" />

      <div className="relative mx-auto w-full max-w-7xl px-6 pb-36 pt-40">
        <motion.p
          key={slide.name}
          data-testid="hero-destination-label"
          className="font-accent text-sm font-bold uppercase tracking-[0.35em] text-gold"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          Study in {slide.short} · {slide.city}
        </motion.p>
        <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
          <MaskedLine delay={0.35}>Study Abroad.</MaskedLine>
          <MaskedLine delay={0.47}>Build Your <span className="italic text-gold">Future.</span></MaskedLine>
        </h1>
        <MaskedLine delay={0.68}>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-200 md:text-xl">
            Explore your opportunities with Student First — a smooth, transparent journey from course selection to visa approval.
          </p>
        </MaskedLine>
        <motion.div
          className="mt-10 flex flex-wrap items-center gap-4"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <Magnetic>
            <a
              href="#countries"
              data-testid="hero-explore-button"
              className="group flex items-center gap-2 rounded-full bg-gold px-8 py-4 font-accent text-sm font-bold uppercase tracking-widest shadow-lift transition-colors duration-300 hover:bg-gold-soft"
            >
              Explore Destinations
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </Magnetic>
          <Magnetic>
            <button
              onClick={() => openConsult()}
              data-testid="hero-expert-button"
              className="flex items-center gap-2 rounded-full border border-white/30 px-8 py-4 font-accent text-sm font-semibold uppercase tracking-widest text-white backdrop-blur-sm transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              Talk to an Expert
            </button>
          </Magnetic>
        </motion.div>
        <motion.ul
          className="mt-14 flex flex-wrap gap-x-8 gap-y-3"
          initial="hidden"
          animate="show"
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 1.05 } } }}
          aria-label="Key highlights"
        >
          {HIGHLIGHTS.map((h) => {
            const Icon = ICONS[h.icon];
            return (
              <motion.li
                key={h.label}
                variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
                className="flex items-center gap-2 text-sm text-slate-200"
              >
                <Icon className="h-4 w-4 text-gold" /> {h.label}
              </motion.li>
            );
          })}
        </motion.ul>
      </div>

      <div className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2.5" role="tablist" aria-label="Choose destination slide">
        {DEST_SLIDES.map((s, i) => (
          <button
            key={s.name}
            onClick={() => goTo(i, true)}
            data-testid={`hero-dot-${i}`}
            role="tab"
            aria-selected={i === index}
            aria-label={`Show ${s.name}`}
            className={`h-2.5 rounded-full transition-all duration-500 ${i === index ? "w-8 bg-gold" : "w-2.5 bg-white/40 hover:bg-white/70"}`}
          />
        ))}
      </div>
    </section>
  );
};

const EditorialMarquee = () => (
  <div className="border-y border-line bg-white py-5" aria-hidden="true">
    <Marquee speed={90} gradient={false} pauseOnHover>
      {COUNTRIES.map((c) => (
        <span key={c.name} className="mx-8 flex items-center gap-8 font-heading text-lg font-bold uppercase tracking-[0.3em] text-navy/25">
          Study in {c.name} <span className="text-gold">✦</span>
        </span>
      ))}
    </Marquee>
  </div>
);

const Services = () => (
  <section className="mx-auto max-w-7xl px-6 py-24 md:py-32" data-testid="services-section">
    <FadeUp>
      <p className="font-accent text-xs font-bold uppercase tracking-[0.3em] text-gold">What We Do</p>
      <h2 className="mt-4 max-w-2xl font-heading text-3xl font-extrabold tracking-tight text-navy sm:text-5xl">
        Everything your journey needs, <span className="italic text-gold">under one roof</span>
      </h2>
    </FadeUp>
    <Stagger className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {SERVICES.map((s, i) => {
        const Icon = ICONS[s.icon];
        return (
          <StaggerItem key={s.title}>
            <div
              data-testid={`service-card-${i}`}
              className="group relative h-full overflow-hidden rounded-xl border border-line bg-white p-8 shadow-soft transition-all duration-300 hover:-translate-y-1.5 hover:border-gold/50 hover:shadow-lift"
            >
              <span className="absolute inset-x-0 top-0 h-0.5 origin-left scale-x-0 bg-gold transition-transform duration-500 group-hover:scale-x-100" />
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-navy/5 text-navy transition-colors duration-300 group-hover:bg-gold/15 group-hover:text-gold">
                <Icon className="h-6 w-6" strokeWidth={1.75} />
              </span>
              <h3 className="mt-6 font-heading text-lg font-bold text-navy">{s.title}</h3>
              <p className="mt-2 text-base leading-relaxed text-muted">{s.desc}</p>
            </div>
          </StaggerItem>
        );
      })}
    </Stagger>
  </section>
);

const Stats = () => (
  <section className="relative overflow-hidden bg-navy py-20 text-white md:py-24" data-testid="stats-section">
    <div className="noise-overlay absolute inset-0" aria-hidden="true" />
    <div className="relative mx-auto grid max-w-7xl grid-cols-2 gap-10 px-6 lg:grid-cols-4">
      {STATS.map((s, i) => (
        <FadeUp key={s.label} delay={i * 0.08} className="text-center">
          <p className="font-heading text-5xl font-extrabold text-gold md:text-6xl" data-testid={`stat-value-${i}`}>
            <Counter target={s.value} suffix={s.suffix} />
          </p>
          <p className="mt-3 font-accent text-xs font-semibold uppercase tracking-[0.25em] text-slate-300">{s.label}</p>
        </FadeUp>
      ))}
    </div>
  </section>
);

const Countries = () => {
  const { openConsult } = useConsult();
  return (
    <section id="countries" className="mx-auto max-w-7xl scroll-mt-24 px-6 py-24 md:py-32" data-testid="countries-section">
      <FadeUp className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="font-accent text-xs font-bold uppercase tracking-[0.3em] text-gold">Destinations</p>
          <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-navy sm:text-5xl">
            Nine countries. <span className="italic text-gold">One decision.</span>
          </h2>
        </div>
        <p className="max-w-sm text-base leading-relaxed text-muted">
          Tap a destination to open your free consultation form — pre-filled for that country.
        </p>
      </FadeUp>
      <Stagger className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" gap={0.06}>
        {COUNTRIES.map((c) => (
          <StaggerItem key={c.name}>
            <button
              onClick={() => openConsult(c.name)}
              data-testid={`country-card-${c.name.toLowerCase().replace(/\s/g, "-")}`}
              className="group relative flex w-full items-center gap-5 overflow-hidden rounded-xl border border-line bg-white p-6 text-left shadow-soft transition-all duration-300 hover:-translate-y-1.5 hover:border-gold/60 hover:shadow-lift"
              aria-label={`Book consultation to study in ${c.name}`}
            >
              <span className="relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full border border-line bg-surface">
                <img src={`https://flagcdn.com/w160/${c.code}.png`} alt={`${c.name} flag`} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
              </span>
              <span className="min-w-0">
                <span className="block font-heading text-xl font-bold text-navy">Study in {c.name}</span>
                <span className="mt-1 block truncate text-sm text-muted">{c.tagline}</span>
              </span>
              <ArrowUpRight className="ml-auto h-5 w-5 shrink-0 text-slate-300 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-gold" />
            </button>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
};

const SuccessWall = () => (
  <section className="overflow-hidden border-y border-line bg-white py-24 md:py-28" data-testid="success-photos-section">
    <FadeUp className="mx-auto max-w-7xl px-6">
      <p className="font-accent text-xs font-bold uppercase tracking-[0.3em] text-gold">Visa Success Stories</p>
      <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-navy sm:text-5xl">
        Students who made it <span className="italic text-gold">abroad</span>
      </h2>
    </FadeUp>
    <FadeUp delay={0.15} className="mt-14">
      <Marquee speed={40} pauseOnHover gradient gradientColor="#ffffff" gradientWidth={120}>
        {SUCCESS_PHOTOS.map((src, i) => (
          <div key={i} className="mx-4" data-testid={`success-photo-${i}`}>
            <SmartImage src={src} alt={`Student visa success story ${i + 1}`} className="h-72 w-56 rounded-xl border border-line shadow-soft md:h-80 md:w-64" />
          </div>
        ))}
      </Marquee>
    </FadeUp>
  </section>
);

const Testimonials = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const hovering = useRef(false);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (!emblaApi || reduce) return;
    const id = setInterval(() => {
      if (!hovering.current) emblaApi.scrollNext();
    }, 4000);
    return () => clearInterval(id);
  }, [emblaApi, reduce]);

  return (
    <section className="mx-auto max-w-7xl px-6 py-24 md:py-32" data-testid="testimonials-section">
      <FadeUp className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="font-accent text-xs font-bold uppercase tracking-[0.3em] text-gold">Google Reviews · 5.0 ★</p>
          <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-navy sm:text-5xl">
            Words from our <span className="italic text-gold">students</span>
          </h2>
        </div>
        <div className="flex gap-3">
          <button onClick={() => emblaApi?.scrollPrev()} data-testid="testimonial-prev" aria-label="Previous review"
            className="flex h-12 w-12 items-center justify-center rounded-full border border-line bg-white text-navy transition-colors duration-300 hover:border-gold hover:text-gold">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button onClick={() => emblaApi?.scrollNext()} data-testid="testimonial-next" aria-label="Next review"
            className="flex h-12 w-12 items-center justify-center rounded-full border border-line bg-white text-navy transition-colors duration-300 hover:border-gold hover:text-gold">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </FadeUp>
      <FadeUp delay={0.1} className="mt-14">
        <div
          className="overflow-hidden"
          ref={emblaRef}
          onMouseEnter={() => (hovering.current = true)}
          onMouseLeave={() => (hovering.current = false)}
          role="region"
          aria-label="Student testimonials carousel"
        >
          <div className="flex">
            {TESTIMONIALS.map((t, i) => (
              <div key={t.name} className="min-w-0 flex-[0_0_100%] px-2 md:flex-[0_0_50%] lg:flex-[0_0_33.333%]">
                <figure
                  data-testid={`testimonial-card-${i}`}
                  className="relative flex h-full flex-col rounded-xl border border-line bg-white p-8 shadow-soft transition-transform duration-300 hover:-translate-y-1"
                >
                  <span className="absolute inset-x-8 top-0 h-0.5 bg-gold" />
                  <Quote className="h-8 w-8 text-gold/40" strokeWidth={1.5} />
                  <blockquote className="mt-5 flex-1 text-base leading-relaxed text-muted">"{t.text}"</blockquote>
                  <figcaption className="mt-8 border-t border-line pt-5">
                    <div className="flex gap-1" aria-label="5 star rating">
                      {[...Array(5)].map((_, s) => <Star key={s} className="h-4 w-4 fill-gold text-gold" />)}
                    </div>
                    <p className="mt-3 font-heading text-base font-bold text-navy">{t.name}</p>
                    <p className="text-sm text-muted">{t.program}</p>
                  </figcaption>
                </figure>
              </div>
            ))}
          </div>
        </div>
      </FadeUp>
    </section>
  );
};

const Faq = () => (
  <section className="border-t border-line bg-white py-24 md:py-32" data-testid="faq-section">
    <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12">
      <FadeUp className="lg:col-span-4">
        <p className="font-accent text-xs font-bold uppercase tracking-[0.3em] text-gold">FAQ</p>
        <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
          Questions students <span className="italic text-gold">ask us</span>
        </h2>
        <p className="mt-4 text-base leading-relaxed text-muted">
          Can't find your answer? Message us on WhatsApp — we reply round the clock.
        </p>
      </FadeUp>
      <FadeUp delay={0.1} className="lg:col-span-8">
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-line">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="py-6 text-left font-heading text-lg font-semibold text-navy hover:text-gold hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="pb-6 text-base leading-relaxed text-muted">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </FadeUp>
    </div>
  </section>
);

const Blog = () => (
  <section className="mx-auto max-w-7xl px-6 py-24 md:py-32" data-testid="blog-section">
    <FadeUp>
      <p className="font-accent text-xs font-bold uppercase tracking-[0.3em] text-gold">From Our Blog</p>
      <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-navy sm:text-5xl">
        Read before you <span className="italic text-gold">apply</span>
      </h2>
    </FadeUp>
    <Stagger className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-2">
      {BLOGS.map((b, i) => (
        <StaggerItem key={b.slug}>
          <Link
            to={`/blog/${b.slug}`}
            data-testid={`blog-card-${i}`}
            className="group block overflow-hidden rounded-xl border border-line bg-white shadow-soft transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lift"
          >
            <SmartImage src={b.img} alt={b.title} className="h-56 w-full" />
            <div className="p-8">
              <p className="font-accent text-xs font-semibold uppercase tracking-widest text-gold">{b.date}</p>
              <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-navy transition-colors duration-300 group-hover:text-gold">
                {b.title}
              </h3>
              <span className="mt-5 inline-flex items-center gap-2 font-accent text-xs font-bold uppercase tracking-widest text-navy">
                Read article <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </div>
          </Link>
        </StaggerItem>
      ))}
    </Stagger>
  </section>
);

const CtaBanner = () => {
  const { openConsult } = useConsult();
  return (
    <section className="relative overflow-hidden bg-navy py-24 text-white md:py-32" data-testid="cta-section">
      <div className="noise-overlay absolute inset-0" aria-hidden="true" />
      <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-gold/10 blur-3xl" aria-hidden="true" />
      <div className="relative mx-auto max-w-4xl px-6 text-center">
        <FadeUp>
          <h2 className="font-heading text-3xl font-extrabold tracking-tight sm:text-5xl">
            Start Your <span className="italic text-gold">Global Education</span> Journey
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-slate-300">
            One free consultation is all it takes to know exactly where you stand — and how far you can go.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Magnetic>
              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-whatsapp-button"
                className="flex items-center gap-2 rounded-full bg-gold px-9 py-4 font-accent text-sm font-bold uppercase tracking-widest text-navy shadow-lift transition-colors duration-300 hover:bg-gold-soft"
              >
                <MessageCircle className="h-5 w-5" /> WhatsApp Us Now
              </a>
            </Magnetic>
            <Magnetic>
              <button
                onClick={() => openConsult()}
                data-testid="cta-form-button"
                className="rounded-full border border-white/25 px-9 py-4 font-accent text-sm font-semibold uppercase tracking-widest text-white transition-colors duration-300 hover:border-gold hover:text-gold"
              >
                Fill the Form Instead
              </button>
            </Magnetic>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="home-page"
    >
      <Hero />
      <EditorialMarquee />
      <Services />
      <Stats />
      <Countries />
      <SuccessWall />
      <Testimonials />
      <Faq />
      <Blog />
      <CtaBanner />
    </motion.div>
  );
}
